import React from "react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { AdminCommunityScopeBar } from "@/components/admin/AdminCommunityScopeBar";
import { UserCommunityScopeBar } from "@/components/layout/UserCommunityScopeBar";
import { Button } from "@/components/ui/button";
import { useChangelog } from "@/contexts/ChangelogContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  Activity,
  Building2,
  ChevronDown,
  ChevronUp,
  Megaphone,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface PageHighlight {
  title: string;
  description: string;
  icon: LucideIcon;
}

type Accent = "blue" | "turquoise" | "green" | "purple";

interface PageMeta {
  badge: string;
  accent: Accent;
  highlights: PageHighlight[];
}

const getPageTitle = (pathname: string) => {
  switch (pathname) {
    case "/app":
      return "Dashboard";
    case "/app/communities":
      return "Comunidad";
    case "/app/payments":
      return "Pagos";
    case "/app/incidents":
      return "Incidencias";
    case "/app/meetings":
      return "Juntas";
    case "/app/documents":
      return "Documentación";
    case "/app/reservations":
      return "Reservas";
    case "/app/providers":
      return "Proveedores";
    case "/app/chat":
      return "Chat";
    case "/app/suggestions":
      return "Sugerencias";
    case "/app/announcements":
      return "Anuncios";
    case "/app/quick-polls":
      return "Encuestas";
    case "/app/settings":
      return "Ajustes";
    case "/app/profile":
      return "Perfil";
    case "/app/calendar":
      return "Calendario";
    case "/app/users":
      return "Usuarios";
    case "/app/support":
      return "Soporte";
    case "/app/roles":
      return "Roles";
    case "/app/admin/dashboard":
      return "Panel Admin";
    case "/app/admin/communities":
      return "Comunidades";
    case "/app/admin/campaigns":
      return "Campañas";
    default:
      if (pathname.startsWith("/app/reservations")) {
        return "Reservas";
      }
      if (pathname.startsWith("/app/admin")) {
        return "Administración";
      }
      if (pathname.startsWith("/app/communities/")) {
        return "Comunidad";
      }
      return "Panel";
  }
};

const getPageSubtitle = (pathname: string) => {
  switch (pathname) {
    case "/app":
      return "Un resumen elegante de lo que ocurre en tu comunidad";
    case "/app/communities":
      return "Información centralizada y actualizada de tu comunidad";
    case "/app/payments":
      return "Controla los movimientos económicos y las cuotas pendientes";
    case "/app/incidents":
      return "Gestiona y da seguimiento a las incidencias en tiempo real";
    case "/app/meetings":
      return "Organiza juntas y haz seguimiento a los acuerdos";
    case "/app/documents":
      return "Toda la documentación importante en un solo lugar";
    case "/app/reservations":
      return "Coordina reservas de espacios compartidos sin complicaciones";
    case "/app/providers":
      return "Conecta con proveedores y gestiona sus servicios";
    case "/app/chat":
      return "Conversaciones organizadas para una comunidad conectada";
    case "/app/suggestions":
      return "Recoge ideas y propuestas para mejorar tu comunidad";
    case "/app/announcements":
      return "Comparte las novedades más relevantes con tus vecinos";
    case "/app/quick-polls":
      return "Participa en votaciones y toma decisiones comunitarias rápido";
    case "/app/settings":
      return "Personaliza la experiencia y preferencias de la plataforma";
    case "/app/profile":
      return "Administra tu información personal y de residencia";
    case "/app/calendar":
      return "Eventos, juntas y fechas clave de la comunidad";
    case "/app/users":
      return "Administra miembros, invitaciones y perfiles comunitarios";
    case "/app/support":
      return "Atención al residente y resuelve tus dudas fácilmente";
    case "/app/roles":
      return "Configura permisos y accesos según la responsabilidad";
    case "/app/admin/dashboard":
      return "Métricas globales y control operativo de comunidades";
    case "/app/admin/communities":
      return "Alta, configuración y estado general del portafolio";
    case "/app/admin/campaigns":
      return "Publicación de avisos emergentes y campañas activas";
    default:
      return "Gestión integrada para tu comunidad";
  }
};

const getPageMeta = (pathname: string): PageMeta => {
  if (pathname.startsWith("/app/reservations")) {
    return {
      badge: "Reservas con estilo",
      accent: "turquoise",
      highlights: [
        {
          title: "Espacios comunes",
          description: "Reserva pistas, salas y zonas compartidas en segundos.",
          icon: Building2,
        },
        {
          title: "Transparencia total",
          description: "Visualiza turnos ocupados y disponibilidad en tiempo real.",
          icon: Activity,
        },
        {
          title: "Normas de uso",
          description: "Cumple las reglas automáticas de cada instalación.",
          icon: ShieldCheck,
        },
      ],
    };
  }

  if (pathname.startsWith("/app/admin/dashboard")) {
    return {
      badge: "Visión global",
      accent: "blue",
      highlights: [
        {
          title: "Indicadores",
          description: "Analiza métricas clave de todas las comunidades.",
          icon: Activity,
        },
        {
          title: "Coordinación",
          description: "Anticipa necesidades y asigna recursos estratégicos.",
          icon: Users,
        },
        {
          title: "Confianza",
          description: "Garantiza procesos estandarizados y auditables.",
          icon: ShieldCheck,
        },
      ],
    };
  }

  if (pathname.startsWith("/app/admin/communities")) {
    return {
      badge: "Red de comunidades",
      accent: "green",
      highlights: [
        {
          title: "Panorama",
          description: "Supervisa el estado general de cada comunidad.",
          icon: Building2,
        },
        {
          title: "Gestión",
          description: "Asigna administradores y define prioridades.",
          icon: Users,
        },
        {
          title: "Escalabilidad",
          description: "Integra nuevas comunidades con flujos guiados.",
          icon: Sparkles,
        },
      ],
    };
  }

  if (pathname.startsWith("/app/admin/campaigns")) {
    return {
      badge: "Comunicación segmentada",
      accent: "purple",
      highlights: [
        {
          title: "Impacto inmediato",
          description: "Lanza mensajes clave para residentes al momento de iniciar sesión.",
          icon: Megaphone,
        },
        {
          title: "Segmentación precisa",
          description: "Activa campañas por comunidad y controla su vigencia en segundos.",
          icon: Users,
        },
        {
          title: "Contenido atractivo",
          description: "Combina textos e imágenes para maximizar la visibilidad de tus iniciativas.",
          icon: Sparkles,
        },
      ],
    };
  }

  return {
    badge: "Área de gestión",
    accent: "blue",
    highlights: [
      {
        title: "Seguimiento en vivo",
        description: "Recibe actualizaciones de tu comunidad en tiempo real.",
        icon: Activity,
      },
      {
        title: "Soporte inteligente",
        description: "Accede a herramientas digitales diseñadas para administradores y vecinos.",
        icon: Sparkles,
      },
      {
        title: "Colaboración",
        description: "Mantén a todos conectados con procesos transparentes.",
        icon: Users,
      },
    ],
  };
};

const accentBadgeClasses: Record<Accent, string> = {
  blue: "border border-primary/20 bg-app-accent-soft text-app-accent-strong dark:border-primary/30 dark:bg-primary/15 dark:text-slate-100",
  turquoise: "border border-secondary/25 bg-secondary/10 text-secondary dark:border-secondary/30 dark:bg-secondary/15 dark:text-slate-100",
  green: "border border-emerald-300/40 bg-emerald-50/80 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-slate-100",
  purple: "border border-violet-300/40 bg-violet-50/80 text-violet-700 dark:border-violet-400/30 dark:bg-violet-400/10 dark:text-slate-100",
};

const accentIconClasses: Record<Accent, string> = {
  blue: "border border-primary/20 bg-app-accent-soft text-app-accent-strong dark:border-primary/30 dark:bg-primary/15 dark:text-slate-100",
  turquoise: "border border-secondary/25 bg-secondary/10 text-secondary dark:border-secondary/30 dark:bg-secondary/15 dark:text-slate-100",
  green: "border border-emerald-300/40 bg-emerald-50/80 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-slate-100",
  purple: "border border-violet-300/40 bg-violet-50/80 text-violet-700 dark:border-violet-400/30 dark:bg-violet-400/10 dark:text-slate-100",
};

const accentHighlightClasses: Record<Accent, string> = {
  blue: "border-primary/20 bg-app-accent-soft/50",
  turquoise: "border-secondary/25 bg-secondary/5",
  green: "border-emerald-300/40 bg-emerald-400/5",
  purple: "border-violet-300/40 bg-violet-400/5",
};

export const PageHeader: React.FC = () => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const {
    openChangelog,
    hasUpdates,
    hasSessionUpdates,
    loading: changelogLoading,
  } = useChangelog();
  const isAdmin = currentUser?.role === "admin";

  const pageTitle = getPageTitle(location.pathname);
  const pageSubtitle = getPageSubtitle(location.pathname);
  const meta = getPageMeta(location.pathname);
  const [highlightsCollapsed, setHighlightsCollapsed] = React.useState(true);
  const highlightSectionId = React.useId();
  const canShowHighlights = meta.highlights.length > 0 && !isAdmin;

  // Detección inteligente de scroll para la barra PWA en móvil (Oculta al bajar, aparece rápido al subir)
  const [isHeaderVisible, setIsHeaderVisible] = React.useState(true);
  const lastScrollY = React.useRef(0);

  React.useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY <= 10) {
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY.current + 8) {
        // Scroll hacia abajo -> Ocultar la barra
        setIsHeaderVisible(false);
      } else if (currentScrollY < lastScrollY.current - 8) {
        // Scroll hacia arriba -> Reaparecer rápidamente
        setIsHeaderVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Barra superior de control fija e inteligente para PWA / Móvil */}
      <div
        className={cn(
          "fixed top-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-md border-b border-app-border-soft px-3 py-2 pt-[calc(0.5rem+env(safe-area-inset-top,0px))] shadow-md transition-transform duration-300 ease-in-out lg:hidden",
          isHeaderVisible ? "translate-y-0" : "-translate-y-full"
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <SidebarTrigger className="inline-flex h-9 w-9 items-center justify-center border border-app-border-soft bg-app-surface text-primary transition hover:border-primary/40 hover:bg-app-accent-soft shrink-0" />
            <h1 className="font-serif text-base font-medium text-foreground truncate tracking-tight">
              {pageTitle}
            </h1>
          </div>

          <div className="flex items-center gap-1 border border-app-border-soft bg-app-surface/90 p-1">
            {hasSessionUpdates && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={changelogLoading}
                onClick={() => openChangelog("manual")}
                className="relative px-2 py-0.5 text-[11px] font-semibold tracking-wide text-app-accent-strong transition"
                aria-label="Novedades disponibles, abre el changelog"
              >
                Novedades
                {hasUpdates && (
                  <span className="absolute -right-1 -top-1 inline-flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" aria-hidden="true" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
                  </span>
                )}
              </Button>
            )}
            <ThemeToggle />
            <NotificationBell />
            <LanguageSwitcher />
          </div>
        </div>

        {/* Selectores de Comunidad en la barra móvil */}
        <div className="w-full space-y-1 mt-1.5">
          <UserCommunityScopeBar />
          <AdminCommunityScopeBar />
        </div>
      </div>

      {/* Espaciador en móvil para evitar solapamiento con el inicio del contenido */}
      <div className="h-16 lg:hidden" aria-hidden="true" />

      {/* Cabecera Completa (Título, Descripción, Badges, Highlights) Exclusiva de Escritorio */}
      <div className="hidden lg:block relative overflow-hidden border border-app-border-soft bg-app-panel/95 p-6 shadow-surface transition-colors duration-300">
        <div className="absolute left-6 top-0 h-px w-28 bg-primary/70" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-app-border-soft to-transparent" aria-hidden="true" />
        
        <div className="relative flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2.5">
                <span className={cn(
                  "inline-flex w-fit items-center px-3.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.32em]",
                  accentBadgeClasses[meta.accent]
                )}>
                  {meta.badge}
                </span>
              </div>
              <div className="space-y-2.5">
                <div className="flex flex-col gap-1.5">
                  <h1 className="font-serif text-3xl font-light leading-none text-foreground transition-colors md:text-4xl">
                    {pageTitle}
                  </h1>
                  {!isAdmin && (
                    <p className="max-w-2xl text-sm leading-relaxed text-app-muted transition-colors md:text-[15px]">
                      {pageSubtitle}
                    </p>
                  )}
                </div>
                {canShowHighlights && (
                  <button
                    type="button"
                    onClick={() => setHighlightsCollapsed(prev => !prev)}
                    aria-expanded={!highlightsCollapsed}
                    aria-controls={highlightSectionId}
                    className="inline-flex w-fit items-center gap-1.5 border border-app-border-soft bg-app-surface px-3 py-1 text-[11px] font-semibold text-app-accent-strong transition hover:border-primary/40 hover:bg-app-accent-soft"
                  >
                    {highlightsCollapsed ? (
                      <>
                        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                        Mostrar detalles
                      </>
                    ) : (
                      <>
                        <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                        Ocultar detalles
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-3 w-[460px]">
              <div className="flex items-center gap-1.5 border border-app-border-soft bg-app-surface/90 p-1.5">
                {hasSessionUpdates && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={changelogLoading}
                    onClick={() => openChangelog("manual")}
                    className="relative px-3 py-1 text-xs font-semibold tracking-wide text-app-accent-strong transition disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label="Novedades disponibles, abre el changelog"
                  >
                    Novedades
                    {hasUpdates && (
                      <span className="absolute -right-1 -top-1 inline-flex h-2.5 w-2.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" aria-hidden="true" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" aria-hidden="true" />
                      </span>
                    )}
                  </Button>
                )}
                <ThemeToggle />
                <NotificationBell />
                <LanguageSwitcher />
              </div>

              <div className="w-full max-w-[460px]">
                <UserCommunityScopeBar />
              </div>
            </div>
          </div>

          <AdminCommunityScopeBar />

          {canShowHighlights && !highlightsCollapsed && (
            <div
              id={highlightSectionId}
              aria-hidden={highlightsCollapsed}
              className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-3"
            >
              {meta.highlights.map((highlight) => {
                const Icon = highlight.icon;
                return (
                  <div
                    key={highlight.title}
                    className={cn(
                      "relative overflow-hidden border border-app-border-soft bg-app-surface/80 p-3.5 transition-colors duration-200 hover:border-primary/30 hover:bg-app-panel",
                      accentHighlightClasses[meta.accent]
                    )}
                  >
                    <div className="flex items-start gap-2.5">
                      <span
                        className={cn(
                          "inline-flex h-9 w-9 flex-shrink-0 items-center justify-center",
                          accentIconClasses[meta.accent]
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="space-y-1">
                        <h3 className="text-xs font-semibold leading-tight text-foreground transition-colors md:text-sm">
                          {highlight.title}
                        </h3>
                        <p className="text-[11px] leading-relaxed text-muted-foreground transition-colors md:text-xs">
                          {highlight.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
